import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileDropzone } from '@/components/shared/file-dropzone';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Listbox } from '@/components/ui/listbox';
import { Stepper } from '@/components/ui/stepper';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatNumber } from '@/utils/format';

import {
  buildExpenseCategoryImportMapping,
  EXPENSE_CATEGORY_IMPORT_FIELD_LABEL_KEYS,
  EXPENSE_CATEGORY_IMPORT_FIELDS,
  MAX_EXPENSE_CATEGORY_IMPORT_ROWS,
  parseCsv,
  prepareExpenseCategoryImportRows,
  REQUIRED_EXPENSE_CATEGORY_IMPORT_FIELDS,
  SAMPLE_EXPENSE_CATEGORY_CSV,
  type ExpenseCategoryImportField,
  type ExpenseCategoryImportRowIssue,
  type ExpenseCategoryImportRowPayload,
  type PreparedExpenseCategoryImportRow,
} from './expense-category-import-csv';

type Step = 0 | 1 | 2 | 3;

interface ImportResultRow {
  index: number;
  ok: boolean;
  categoryId?: string;
  error?: string;
  params?: Record<string, unknown>;
}

interface ExpenseCategoryImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const displayLabel = (
  row: PreparedExpenseCategoryImportRow,
  mapping: Record<string, ExpenseCategoryImportField | ''>,
) => {
  if (row.payload) return row.payload.name;
  const nameHeader = Object.entries(mapping).find(([, field]) => field === 'name')?.[0];
  return nameHeader ? (row.source[nameHeader] ?? '—') : '—';
};

export const ExpenseCategoryImportDialog = ({
  open,
  onOpenChange,
}: ExpenseCategoryImportDialogProps) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>(0);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, ExpenseCategoryImportField | ''>>({});
  const [results, setResults] = useState<ImportResultRow[] | null>(null);

  const existingCategories = useQuery({
    queryKey: ['expenses', 'categories', 'import-names'],
    queryFn: async () => {
      const response = await api.get<{ data: Array<{ name: string }> }>('/expenses/categories');
      return new Set(response.data.map((row) => row.name.trim().toLowerCase()));
    },
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const reset = () => {
    setStep(0);
    setFileName(null);
    setFileError(null);
    setHeaders([]);
    setRawRows([]);
    setMapping({});
    setResults(null);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  const prepared = useMemo(
    () =>
      prepareExpenseCategoryImportRows({
        rows: rawRows,
        mapping,
        existingNames: existingCategories.data ?? new Set<string>(),
      }),
    [rawRows, mapping, existingCategories.data],
  );

  const readyRows = prepared.filter(
    (row): row is PreparedExpenseCategoryImportRow & { payload: ExpenseCategoryImportRowPayload } =>
      Boolean(row.payload),
  );

  const mappedRequired = REQUIRED_EXPENSE_CATEGORY_IMPORT_FIELDS.every((field) =>
    Object.values(mapping).includes(field),
  );

  const fieldOptions = [
    { value: '', label: t('categories.import.fields.skip') },
    ...EXPENSE_CATEGORY_IMPORT_FIELDS.map((field) => ({
      value: field,
      label: t(EXPENSE_CATEGORY_IMPORT_FIELD_LABEL_KEYS[field]),
    })),
  ];

  const issueLabel = (issue: ExpenseCategoryImportRowIssue) =>
    t(`categories.import.issues.${issue}`);

  const importMutation = useMutation({
    mutationFn: (rows: ExpenseCategoryImportRowPayload[]) =>
      api.post<{ results: ImportResultRow[] }>('/expenses/categories/import', { rows }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['expenses', 'categories'] });
      void queryClient.invalidateQueries({ queryKey: ['expenses', 'insights'] });
      const succeeded = data.results.filter((row) => row.ok).length;
      if (succeeded > 0 && data.results.every((row) => row.ok)) {
        toast.success(t('categories.import.toast.done', { count: succeeded }));
        onOpenChange(false);
        return;
      }
      toast.success(t('categories.import.toast.done', { count: succeeded }));
      setResults(data.results);
      setStep(3);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const onFiles = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setFileError(null);

    if (!file.name.toLowerCase().endsWith('.csv') && file.type !== 'text/csv') {
      setFileError(t('categories.import.upload.invalidType'));
      return;
    }

    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.headers.length === 0 || parsed.rows.length === 0) {
        setFileError(t('categories.import.upload.empty'));
        return;
      }
      if (parsed.rows.length > MAX_EXPENSE_CATEGORY_IMPORT_ROWS) {
        setFileError(
          t('categories.import.upload.tooMany', { max: formatNumber(MAX_EXPENSE_CATEGORY_IMPORT_ROWS) }),
        );
        return;
      }
      setFileName(file.name);
      setHeaders(parsed.headers);
      setRawRows(parsed.rows);
      setMapping(buildExpenseCategoryImportMapping(parsed.headers));
      setStep(1);
    } catch {
      setFileError(t('categories.import.upload.readFailed'));
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_EXPENSE_CATEGORY_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'expense-categories-import-sample.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const steps = [
    { id: 'upload', label: t('categories.import.steps.upload') },
    { id: 'map', label: t('categories.import.steps.map') },
    { id: 'preview', label: t('categories.import.steps.preview') },
    { id: 'results', label: t('categories.import.steps.results') },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>{t('categories.import.title')}</DialogTitle>
          <DialogDescription>{t('categories.import.description')}</DialogDescription>
        </DialogHeader>

        <Stepper
          label={t('categories.import.stepperLabel')}
          current={step}
          steps={steps}
          className="shrink-0"
        />

        <DialogBody>
          {step === 0 ? (
            <div className="space-y-4">
              <p className="text-body-sm text-fg-secondary">
                {t('categories.import.upload.notExpenses')}
              </p>
              <FileDropzone
                accept=".csv,text/csv"
                acceptLabel={t('categories.import.upload.accept')}
                maxBytes={2 * 1024 * 1024}
                maxSizeLabel={t('categories.import.upload.maxSize')}
                error={fileError ?? undefined}
                labels={{
                  prompt: t('categories.import.upload.prompt'),
                  remove: tCommon('action.remove'),
                }}
                files={fileName ? [{ name: fileName }] : undefined}
                onRemove={() => {
                  setFileName(null);
                  setHeaders([]);
                  setRawRows([]);
                  setMapping({});
                  setFileError(null);
                }}
                onFiles={(files) => {
                  void onFiles(files);
                }}
              />
              <div className="flex justify-end">
                <Button type="button" variant="ghost" size="sm" onClick={downloadSample}>
                  {t('categories.import.upload.downloadSample')}
                </Button>
              </div>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="space-y-4">
              <p className="text-body-sm text-fg-secondary">{t('categories.import.map.help')}</p>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[28rem] text-left text-body-sm">
                  <thead className="bg-surface-sunken text-fg-secondary">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('categories.import.map.csvColumn')}
                      </th>
                      <th className="px-3 py-2 font-medium">
                        {t('categories.import.map.trinityField')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {headers.map((header) => (
                      <tr key={header} className="border-t border-border">
                        <td className="px-3 py-2 align-middle font-medium text-fg">
                          {header || '—'}
                        </td>
                        <td className="px-3 py-2 align-middle">
                          <Listbox
                            id={`category-map-${header}`}
                            value={mapping[header] ?? ''}
                            onChange={(value) => {
                              const nextField = (value ?? '') as ExpenseCategoryImportField | '';
                              setMapping((prev) => {
                                const next = { ...prev, [header]: nextField };
                                if (nextField) {
                                  for (const key of Object.keys(next)) {
                                    if (key !== header && next[key] === nextField) next[key] = '';
                                  }
                                }
                                return next;
                              });
                            }}
                            options={fieldOptions}
                            searchable
                            searchPlaceholder={t('categories.import.map.searchFields')}
                            emptyMessage={t('categories.import.map.noFieldMatch')}
                            popoverPortal="dialog"
                            clearable={false}
                            aria-label={t('categories.import.map.trinityFieldForColumn', {
                              column: header,
                            })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!mappedRequired ? (
                <p role="alert" className="text-caption text-danger-fg">
                  {t('categories.import.map.requiredMissing')}
                </p>
              ) : null}
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-4">
              <p className="text-body-sm text-fg-secondary">
                {t('categories.import.preview.summary', {
                  ready: readyRows.length,
                  problems: prepared.filter((row) => row.issues.length > 0).length,
                })}
              </p>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full min-w-[32rem] text-left text-body-sm">
                  <thead className="bg-surface-sunken text-fg-secondary">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('categories.columns.name')}</th>
                      <th className="px-3 py-2 font-medium">
                        {t('categories.import.fields.parentCategory')}
                      </th>
                      <th className="px-3 py-2 font-medium">
                        {t('categories.import.preview.status')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {prepared.slice(0, 50).map((row) => (
                      <tr key={row.index} className="border-t border-border">
                        <td className="px-3 py-2 font-medium text-fg">
                          {displayLabel(row, mapping)}
                        </td>
                        <td className="px-3 py-2 text-fg-secondary">
                          {row.payload?.parentCategoryName ?? '—'}
                        </td>
                        <td className="px-3 py-2">
                          {row.issues.length === 0 ? (
                            <Badge variant="success">{t('categories.import.preview.ready')}</Badge>
                          ) : (
                            <span className="text-caption text-danger-fg">
                              {row.issues.map(issueLabel).join(' · ')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {prepared.length > 50 ? (
                <p className="text-caption text-fg-tertiary">
                  {t('categories.import.preview.truncated', {
                    shown: formatNumber(50),
                    total: formatNumber(prepared.length),
                  })}
                </p>
              ) : null}
            </div>
          ) : null}

          {step === 3 && results ? (
            <div className="space-y-4">
              <p className="text-body-sm text-fg-secondary">
                {t('categories.import.results.summary', {
                  succeeded: results.filter((row) => row.ok).length,
                  failed: results.filter((row) => !row.ok).length,
                })}
              </p>
              {results.some((row) => !row.ok) ? (
                <ul className="space-y-2">
                  {results
                    .filter((row) => !row.ok)
                    .slice(0, 40)
                    .map((row) => {
                      const preparedRow = prepared.find(
                        (item) => item.index === row.index && item.payload,
                      );
                      const label = preparedRow?.payload
                        ? preparedRow.payload.name
                        : t('categories.import.results.rowLabel', {
                            index: formatNumber(row.index + 1),
                          });
                      return (
                        <li
                          key={row.index}
                          className="rounded-md border border-border px-3 py-2 text-body-sm"
                        >
                          <span className="font-medium text-fg">{label}</span>
                          <span className="mt-0.5 block text-caption text-danger-fg">
                            {row.error
                              ? tErrors(row.error, row.params)
                              : tErrors('EXPENSE_CATEGORY_IMPORT_ROW_FAILED')}
                          </span>
                        </li>
                      );
                    })}
                </ul>
              ) : (
                <p className="text-body-sm text-fg">{t('categories.import.results.allGood')}</p>
              )}
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter>
          {step === 0 ? (
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {tCommon('action.cancel')}
            </Button>
          ) : null}

          {step === 1 ? (
            <>
              <Button type="button" variant="outline" onClick={() => setStep(0)}>
                {tCommon('action.back')}
              </Button>
              <Button type="button" disabled={!mappedRequired} onClick={() => setStep(2)}>
                {tCommon('action.next')}
              </Button>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                disabled={importMutation.isPending}
              >
                {tCommon('action.back')}
              </Button>
              <Button
                type="button"
                loading={importMutation.isPending}
                disabled={readyRows.length === 0}
                onClick={() => importMutation.mutate(readyRows.map((row) => row.payload))}
              >
                {t('categories.import.preview.confirm', { count: readyRows.length })}
              </Button>
            </>
          ) : null}

          {step === 3 ? (
            <Button type="button" onClick={() => onOpenChange(false)}>
              {tCommon('action.close')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
